import { useQuery } from '@tanstack/react-query';
import { paymentsNamespace } from 'receivable-invoices-js/constants/hosts';
import { useSentry } from 'receivable-invoices-js/react/hooks/use-sentry';
import { shouldSendToSentry } from 'receivable-invoices-js/react/utils/network';
import { qontoClient } from 'receivable-invoices-js/react/utils/api-clients/qonto';

type PaymentStatus = 'open' | 'paid' | 'failed' | 'canceled' | 'expired';

interface Payment {
  status: PaymentStatus;
}

interface PaymentResponse {
  data: {
    attributes: Payment;
  };
}

/**
 * Polling status for payment
 * 'idle' - polling not started
 * 'polling' - currently polling
 * 'success' - payment successful
 * 'failure' - payment failed
 * 'timeout' - polling timed out / network failure
 */
export type PaymentPollingStatus = 'idle' | 'polling' | 'success' | 'failure' | 'timeout';

interface UseStatusPollingParams {
  paymentId: string | null;
  enabled?: boolean;
}

export function useStatusPolling({
  paymentId,
  enabled,
}: UseStatusPollingParams): PaymentPollingStatus {
  const sentry = useSentry();

  const { data, isError, failureReason, isFetching } = useQuery({
    queryKey: ['payment-polling', paymentId],
    queryFn: async () => {
      const response = await qontoClient<PaymentResponse>(`${paymentsNamespace}/${paymentId}`, {
        method: 'GET',
      });

      const payment = response.data.attributes;

      // Throw error for non-successful statuses to trigger retry
      if (['open', 'pending'].includes(payment.status)) {
        throw new Error('Payment still open');
      }

      return payment;
    },
    enabled,
    retry: 5,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  });

  // Send errors to Sentry
  if (isError && failureReason && shouldSendToSentry(failureReason)) {
    sentry.captureException(failureReason);
  }

  if (!paymentId) return 'idle';
  if (isFetching) return 'polling';
  if (isError) return 'timeout';
  if (data?.status === 'paid') return 'success';
  return 'failure';
}
